import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QRCode from "react-qr-code";
import {
    FileText,
    Download,
    Eye,
    Calendar,
    BookOpen,
    Sparkles,
    ChevronRight,
    Scale,
    Clock
} from 'lucide-react';

function CategoriesProduct() {
    const [newsList, setNews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/documents-terkini`);
                const datas = await response.json();
                setNews(datas.data || []);
            } catch (error) {
                console.error('Error fetching documents:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('id-ID', options);
    };

    // Truncate text
    const truncateText = (text, maxLength = 60) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    return (
        <div className="w-full bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0889c1]/10 to-purple-500/10 rounded-full mb-4">
                        <Sparkles className="w-4 h-4 text-[#0889c1]" />
                        <span className="text-xs font-medium text-gray-600">JDIH Kota Magelang</span>
                    </div>

                    <div className="flex items-center justify-center gap-3 mb-3">
                        <Scale className="w-8 h-8 text-[#0889c1]" />
                        <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'MuseoSansRounded900' }}>
                            <span className="text-[#0889c1]">PERATURAN</span>
                            <span className="text-gray-700"> TERBARU</span>
                        </h1>
                    </div>

                    <div className="w-24 h-1 bg-gradient-to-r from-[#0889c1] to-purple-500 mx-auto rounded-full"></div>

                    <p className="mt-4 text-gray-600 text-sm max-w-2xl mx-auto">
                        Daftar peraturan yang baru saja disahkan dan diterbitkan
                    </p>
                </div>

                {/* Loading State */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#0889c1]"></div>
                    </div>
                ) : (
                    <>
                        {/* Documents Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {newsList.length > 0 ? (
                                newsList.map((field, index) => (
                                    <div
                                        key={index}
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                        className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                                    >
                                        <div className="p-6">
                                            <div className="flex items-start gap-4">
                                                {/* QR Code Section */}
                                                <div className="flex-shrink-0">
                                                    <div className="bg-gradient-to-br from-gray-50 to-blue-50/50 p-2 rounded-xl group-hover:from-blue-50 group-hover:to-purple-50 transition-all duration-300">
                                                        <div className="w-20 h-20">
                                                            <QRCode
                                                                size={256}
                                                                style={{
                                                                    height: "auto",
                                                                    maxWidth: "100%",
                                                                    width: "100%"
                                                                }}
                                                                value={`${process.env.REACT_APP_BASEURL}/${field.file_url}`}
                                                                viewBox={`0 0 256 256`}
                                                                bgColor="#ffffff"
                                                                fgColor="#0889c1"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content Section */}
                                                <div className="flex-1 min-w-0">
                                                    {/* Title */}
                                                    <h2 className="font-bold text-gray-800 text-base leading-snug group-hover:text-[#0889c1] transition-colors duration-200 line-clamp-2">
                                                        {field.nama}
                                                    </h2>

                                                    {/* Meta Information */}
                                                    <div className="mt-2 space-y-1.5">
                                                        {field.no_peraturan && (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                                <BookOpen className="w-3.5 h-3.5 text-[#0889c1]" />
                                                                <span className="font-medium">No. {field.no_peraturan}</span>
                                                            </div>
                                                        )}

                                                        {field.tgl_penetapan && (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                                <Calendar className="w-3.5 h-3.5 text-[#0889c1]" />
                                                                <span>{formatDate(field.tgl_penetapan)}</span>
                                                            </div>
                                                        )}

                                                        {field.content && (
                                                            <div className="flex items-start gap-1.5 text-xs text-gray-500">
                                                                <FileText className="w-3.5 h-3.5 text-[#0889c1] flex-shrink-0 mt-0.5" />
                                                                <span className="line-clamp-2">{truncateText(field.content, 80)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                                        <Link
                                                            to={`/detail-produk-hukum/${field.idph}`}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0889c1]/10 text-[#0889c1] text-xs font-semibold rounded-lg hover:bg-[#0889c1] hover:text-white transition-all duration-200 group-hover:shadow-md"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            <span>Lihat</span>
                                                        </Link>

                                                        <a
                                                            href={`${process.env.REACT_APP_BASEURL}/${field.file_url}`}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-600 text-xs font-semibold rounded-lg hover:bg-green-500 hover:text-white transition-all duration-200 group-hover:shadow-md"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            download
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                            <span>Unduh</span>
                                                        </a>

                                                        {/* Status Badge */}
                                                        {field.status && (
                                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded-full">
                                                                {field.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Decorative Gradient Line */}
                                            <div className="mt-4 h-0.5 bg-gradient-to-r from-[#0889c1]/20 via-purple-500/20 to-transparent rounded-full group-hover:from-[#0889c1] group-hover:via-purple-500 transition-all duration-300"></div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-lg">
                                    <Scale className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-gray-700 mb-2">Belum Ada Peraturan</h3>
                                    <p className="text-gray-500">Belum ada peraturan terbaru yang tersedia</p>
                                </div>
                            )}
                        </div>

                        {/* View All Button */}
                        {newsList.length > 0 && (
                            <div className="mt-10 text-center">
                                <Link to="/pencarian-produk-hukum/peraturan-daerah">
                                    <button className="group inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#0889c1] to-purple-600 text-white text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <span>Lihat Semua Produk Hukum</span>
                                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                                    </button>
                                </Link>
                            </div>
                        )}

                        {/* Footer Info */}
                        
                    </>
                )}
            </div>
        </div>
    );
}

export default CategoriesProduct;